import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bell, ShieldCheck, TrendingUp, Eye, Rocket, Activity, Coffee, Zap, LineChart } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import useSeo from "../hooks/useSeo";

/**
 * Features — long-form feature page.
 * Audit flagged /features rendering as an empty shell (D2.4 partial).
 * This route now serves real content rather than a blank SPA fallback.
 */
export default function Features() {
  useSeo({
    title: "Features — Moonbucks — Analyst-vetted stock alerts",
    description: "Real-time screener, plain-English alerts, human-analyst review, public track record and $10K portfolio simulation.",
    canonical: "https://moonbucks.io/features",
  });

  const features = [
    { icon: TrendingUp, title: "Real-time breakout screener", body: "Our desk continuously scans ~300 U.S. equities for volume surges, breakouts, institutional flow and momentum. You see the shortlist — not the noise." },
    { icon: Eye, title: "Analyst-vetted before publish", body: "Every candidate our scanner surfaces gets eyeballed by a human before it hits your inbox. No pump-and-dump auto-fires." },
    { icon: Bell, title: "Plain-English alerts", body: "Every alert reads like a paragraph, not a chart. Ticker, conviction, why it fired, TP1 / Moon / Stop targets, and the market backdrop." },
    { icon: Rocket, title: "Two strategies, one subscription", body: "Steady = high-hit-rate confirmation (TP1 +2.5% / Moon +12% / Stop −7%). Moonshot = pre-breakout hunter (TP1 +6% / Moon +20% / Stop −10%)." },
    { icon: ShieldCheck, title: "Public track record", body: "Every alert, entry, exit, and reason auditable at /track-record. Wins and losses. Nothing scrubbed." },
    { icon: LineChart, title: "$10,000 portfolio simulation", body: "See what a subscriber who followed every alert with realistic retail rules would have today — full audit at /portfolio." },
    { icon: Zap, title: "Push + email + Discord delivery", body: "Alerts hit every channel you pick within seconds of the analyst approving them." },
    { icon: Coffee, title: "Custom scenario picker (subscribers)", body: "Hand-pick which alerts belong in YOUR portfolio and re-run the same simulation math on your account." },
  ];

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-6" data-testid="features-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>

        <div className="label-uppercase text-mb-emerald mb-3">Features</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">
          Everything Moonbucks does — one page.
        </h1>
        <p className="mt-4 text-neutral-600 text-lg max-w-2xl leading-relaxed">
          Built for busy investors, not chart nerds. Every feature below exists to keep the signal high and the noise low.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="features-grid">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-white border border-mb-border rounded-lg p-6">
                <div className="w-10 h-10 rounded-full bg-mb-emerald/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-mb-emerald" />
                </div>
                <h2 className="mt-4 font-display font-semibold text-xl">{f.title}</h2>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white border border-mb-border rounded-lg p-6">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-mb-emerald" />
            <div>
              <div className="font-display font-semibold">Ready to see it live?</div>
              <div className="text-xs text-neutral-500">$4.99/week. Cancel anytime.</div>
            </div>
          </div>
          <Link to="/register">
            <Button className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md" data-testid="features-cta">
              Start brewing alerts <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
